import * as find from 'lodash/find'
import { focus } from 'redux-form'
import { FORM_CREATE_SPENDING_TRACKER, FORM_CREATE_INCOME_TRACKER } from 'constants/forms'
import { State } from 'interfaces/state'
import { StoreDispatch } from 'interfaces/component'
import { CategoryId } from 'interfaces/meta'
import MoneyflowBreakdown, { MoneyflowBreakdownStateProps, MoneyflowBreakdownDispatchProps } from 'components/features/pfm/MoneyflowBreakdown/MoneyflowBreakdown'
import { setBreakdownVisibilityActionCreator } from 'actions/moneyflowActions'
import { getMetaCategoriesByType } from 'helpers/categoriesHelpers'
import { selectedPeergroupIdSelector } from 'selectors/peergroupSelectors'
import { Guards } from 'interfaces/guards'
import { connectShadedLoadable, createParallelGuard, bindGuard } from 'helpers/loadableHelpers'
import { peergroupsGuard } from 'guards/globalGuards'
import { memberMoneyflowBreakdownGuard, peergroupsMoneyflowBreakdownGuard, moneyflowTrackersGuard, memberMoneyflowTrendGuard } from 'guards/moneyflowGuards'
import { ALL_CATEGORY_ID } from 'constants/meta'
import { createForgottenGuardError } from 'helpers/errorHelpers'
import { modifyFloatingPaneActionCreator } from 'actions/floatingPanesActions'
import { MoneyflowBreakdownOwnProps } from 'interfaces/moneyflow'
import { withComparisonSelector } from 'components/features/pageFilters/selectors'

type MoneyflowBreakdownModuleContainerOwnProps =
  & MoneyflowBreakdownOwnProps
  & {
    categoryId: CategoryId
  }

const mapStateToProps = (state: State, { type }: MoneyflowBreakdownModuleContainerOwnProps): MoneyflowBreakdownStateProps => {
  const { data, app } = state
  const { peergroups: { yourPeergroupId }, meta } = data

  const selectedPeergroupId = selectedPeergroupIdSelector(state)
  const selectedRelation = find(data.peergroups.relations.byKey, { peergroupId: selectedPeergroupId })
  const timeframe = app[type].timeframe
  const memberBreakdown = data[type].breakdown[timeframe]
  const peersBreakdown = data.peergroups[type].breakdown[timeframe]
  const categories = getMetaCategoriesByType(type, meta)

  const incomeTrend = data.income.trends[timeframe][ALL_CATEGORY_ID]
  if (!incomeTrend) {
    throw createForgottenGuardError('MoneyflowBreakdownModuleContainer', 'incomeTrendGuard')
  }

  const withComparison = withComparisonSelector(state)

  return {
    trackers: data[type].trackers,
    memberValues: memberBreakdown,
    peerValues: peersBreakdown.byKey[yourPeergroupId as number].breakdown,
    otherValues: peersBreakdown.byKey[selectedPeergroupId as number].breakdown,
    isExpanded: app[type].breakdown.isExpanded,
    selectedCategoryId: app[type].categories.selectedId,
    categories,
    timeframe,
    selectedRelation,
    withComparison,
  }
}


const mapDispatchToProps = (dispatch: StoreDispatch, { type, timeframe }: MoneyflowBreakdownModuleContainerOwnProps): MoneyflowBreakdownDispatchProps => {
  const formName = type === 'spending' ? FORM_CREATE_SPENDING_TRACKER : FORM_CREATE_INCOME_TRACKER

  return {
    setExpanded: (isVisible) => dispatch(
      setBreakdownVisibilityActionCreator(type, isVisible)
    ),
    setExpandedCategory: (category) => dispatch(
      modifyFloatingPaneActionCreator('tracker', {
        type: 'tracker',
        payload: {
          category,
          type,
          timeframe,
        },
      })
    ),
    focusLimitField: () => dispatch(focus(formName, 'limit')),
  }
}

const moneyflowBreakdownGuards: Guards<MoneyflowBreakdownOwnProps> = [
  peergroupsGuard,
  createParallelGuard(
    memberMoneyflowBreakdownGuard, peergroupsMoneyflowBreakdownGuard,
    moneyflowTrackersGuard, memberMoneyflowTrendGuard,
    bindGuard(memberMoneyflowTrendGuard, { type: 'income', categoryId: ALL_CATEGORY_ID })
  ),
]

export default connectShadedLoadable<
  MoneyflowBreakdownStateProps,
  MoneyflowBreakdownDispatchProps,
  MoneyflowBreakdownOwnProps,
  MoneyflowBreakdownModuleContainerOwnProps
>(mapStateToProps, mapDispatchToProps, moneyflowBreakdownGuards)(MoneyflowBreakdown)

